import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAccounts } from '../lib/api/accounts'
import { useOrganizations } from '../lib/api/organizations'

const STORAGE_KEY = 'finickly.activeAccountId'

type ActiveOrgContext = {
  activeAccountId: string | 'all'
  setActiveAccountId: (id: string | 'all') => void
  activeOrgId: string | null
}

const Ctx = createContext<ActiveOrgContext | null>(null)

export function ActiveOrgProvider({ children }: { children: ReactNode }) {
  const accounts = useAccounts()
  const organizations = useOrganizations()

  const [activeAccountId, setActiveAccountIdState] = useState<string | 'all'>(() => {
    if (typeof localStorage === 'undefined') return 'all'
    return localStorage.getItem(STORAGE_KEY) ?? 'all'
  })

  useEffect(() => {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(STORAGE_KEY, activeAccountId)
  }, [activeAccountId])

  useEffect(() => {
    if (activeAccountId === 'all') return
    if (!accounts.data) return
    if (!accounts.data.some((a) => a.id === activeAccountId)) {
      setActiveAccountIdState('all')
    }
  }, [accounts.data, activeAccountId])

  const activeOrgId = useMemo(() => {
    if (activeAccountId === 'all') return null
    const acc = accounts.data?.find((a) => a.id === activeAccountId)
    if (!acc) return null
    return organizations.data?.find((o) => o.id === acc.organizationId)?.id ?? null
  }, [accounts.data, organizations.data, activeAccountId])

  const value = useMemo<ActiveOrgContext>(
    () => ({
      activeAccountId,
      setActiveAccountId: setActiveAccountIdState,
      activeOrgId,
    }),
    [activeAccountId, activeOrgId],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useActiveOrg(): ActiveOrgContext {
  const v = useContext(Ctx)
  if (!v) throw new Error('useActiveOrg must be used within ActiveOrgProvider')
  return v
}
